import { unsafeCSS, html } from "lit";
import { property } from "lit/decorators.js";
import { FormControlMixin } from "../../utilities/mixins/form-control.js";
import { emailValidator, requiredValidator, minLengthValidator, maxLengthValidator, patternValidator, urlValidator } from "../../utilities/validators.js";
import { ValidationController } from "../../utilities/controllers/validation.js";
import { LeafElement } from "../leaf-element.js";
import "../icon/icon.js";
import "../../utilities/i18n.js";
import "@shoelace-style/localize";
import "../../utilities/translations/en.js";
import "../../utilities/events.js";
import "../icon/icon.component.js";
import "lit/directives/unsafe-svg.js";
import "../../utilities/cache-fetch.js";
import "../../utilities/watch.js";
import "../icon/library.js";
import "../icon/iconset.system.js";
const styles = "/*------------------------------------*\\\n    #RESET\n\\*------------------------------------*/\n/**\n * Border-Box http:/paulirish.com/2012/box-sizing-border-box-ftw/\n */\n*,\n::slotted(*),\n*:before,\n*:after {\n  box-sizing: border-box;\n}\n\n:host *,\n:host *::before,\n:host *::after {\n  box-sizing: inherit;\n}\n\n[hidden] {\n  display: none !important;\n}\n\n:host {\n  display: block;\n  position: relative;\n  width: -moz-fit-content;\n  width: fit-content;\n}\n:host .leaf-c-input__wrapper {\n  display: flex;\n  flex-direction: column;\n}\n:host ::slotted([slot=input]) {\n  font-family: var(--leaf-typography-body-default-font-family);\n  font-size: var(--leaf-typography-body-default-font-size);\n  font-weight: var(--leaf-typography-body-default-font-weight);\n  line-height: var(--leaf-typography-body-default-line-height);\n  -webkit-text-decoration: var(--leaf-typography-body-default-text-decoration);\n          text-decoration: var(--leaf-typography-body-default-text-decoration);\n  text-transform: var(--leaf-typography-body-default-text-transform);\n  border: var(--leaf-border-width-default) solid var(--leaf-color-border-strong);\n  border-radius: var(--leaf-spacing-8);\n  height: calc(var(--leaf-spacing-16) * 3.5);\n  padding: var(--leaf-spacing-16);\n}\n:host ::slotted([slot=input]:focus),\n:host ::slotted([slot=input]:active) {\n  outline-offset: var(--leaf-border-width-button-default);\n  outline: var(--leaf-border-width-button-default) solid var(--leaf-color-border-active-outline);\n}\n:host ::slotted([slot=input]:focus-visible) {\n  outline-offset: var(--leaf-border-width-button-default) !important;\n}\n:host ::slotted([slot=input][invalid]) {\n  border-color: var(--leaf-color-border-error);\n  outline-color: var(--leaf-color-border-error);\n}\n:host ::slotted([slot=input][valid]) {\n  border-color: var(--leaf-color-border-success);\n  outline-color: var(--leaf-color-border-success);\n}\n:host ::slotted([slot=input]:-moz-read-only) {\n  background-color: var(--leaf-color-bg-disabled);\n  border-color: var(--leaf-color-border-disabled);\n  color: var(--leaf-color-content-disabled) !important;\n  pointer-events: none;\n}\n:host ::slotted([slot=input]:disabled),\n:host ::slotted([slot=input]:read-only) {\n  background-color: var(--leaf-color-bg-disabled);\n  border-color: var(--leaf-color-border-disabled);\n  color: var(--leaf-color-content-disabled) !important;\n  pointer-events: none;\n}\n:host ::slotted([slot=input]:-moz-read-only) {\n  color: initial;\n}\n:host ::slotted([slot=input]:read-only) {\n  color: initial;\n}\n:host ::slotted([slot=note]) {\n  margin-top: var(--leaf-spacing-4);\n}\n:host leaf-icon[name=check-circle] {\n  --icon-height: var(--leaf-spacing-24);\n  color: var(--leaf-color-content-success);\n  position: absolute;\n  right: var(--leaf-spacing-16);\n  top: var(--leaf-spacing-16);\n}";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};
var _onKeydown, _validation, _onInput, onInput_fn;
let nextUniqueId = 0;
class LeafInput extends FormControlMixin(LeafElement) {
  constructor() {
    super(...arguments);
    __privateAdd(this, _onInput);
    __privateAdd(this, _onKeydown, (event) => {
      var _a;
      if (event.key === "Enter") {
        (_a = this.input.form) == null ? void 0 : _a.requestSubmit();
      }
    });
    this.internals = this.attachInternals();
    __privateAdd(this, _validation, new ValidationController(this, {
      validators: [
        emailValidator,
        requiredValidator,
        minLengthValidator,
        maxLengthValidator,
        patternValidator,
        urlValidator
      ],
      nativeTarget: () => this.input
    }));
    this.type = "text";
  }
  get input() {
    return this.querySelector("input");
  }
  get note() {
    return this.querySelector('[slot="note"]');
  }
  connectedCallback() {
    super.connectedCallback();
    this.insertAdjacentHTML("afterbegin", this.createInput());
    __privateGet(this, _validation).validate(this.value ? this.value : void 0);
  }
  async updated(changedProperties) {
    var _a;
    await this.updateComplete;
    if (this.input) {
      for (const [key, value] of changedProperties) {
        if (typeof value !== "undefined") {
          const property2 = String(key);
          if (this[property2]) {
            this.input.setAttribute(property2, String(this[property2]));
          } else {
            this.input.removeAttribute(property2);
          }
        }
      }
      this.input.removeEventListener("keydown", __privateGet(this, _onKeydown));
      (_a = this.input).id || (_a.id = `leaf-input-${nextUniqueId++}`);
      if (this.note) {
        this.note.id = `${this.input.id}-note`;
        this.input.setAttribute("aria-describedby", this.note.id);
      }
      this.input.addEventListener("keydown", __privateGet(this, _onKeydown));
      this.setFormControlElement(this.input);
    }
  }
  createInput() {
    return `<input
      class="leaf-c-input__input"
      slot="input"
      ${this.id ? `id="${this.id}"` : ""}
      ${this.type ? `type="${this.type}"` : ""}
      ${this.required ? "required" : ""}
      ${this.readonly ? "readonly" : ""}
      ${this.disabled ? "disabled" : ""}
      ${this.invalid ? `invalid="${this.invalid}"` : ""}
      ${this.valid ? `valid="${this.valid}"` : ""}
      ${this.placeholder ? `placeholder="${this.placeholder}"` : ""}
      ${this.pattern ? `pattern="${this.pattern}"` : ""}
      ${this.maxLength ? `maxlength="${this.maxLength}"` : ""}
      ${this.minLength ? `minlength="${this.minLength}"` : ""}
      ${this.max ? `max="${this.max}"` : ""}
      ${this.min ? `min="${this.min}"` : ""}
      ${this.name ? `name="${this.name}"` : ""}
      ${this.value ? `value="${this.value}"` : ""}
    />`;
  }
  render() {
    return html`
      <div @input=${__privateMethod(this, _onInput, onInput_fn)} class="leaf-c-input__wrapper">
        <slot @keydown=${__privateGet(this, _onKeydown)} name="input"></slot>
        <slot name="suffix">
          ${this.valid ? html`<leaf-icon set="system" name="check-circle"></leaf-icon>` : null}
        </slot>
        <slot name="note"></slot>
      </div>
      ${__privateGet(this, _validation).render()}
    `;
  }
}
_onKeydown = /* @__PURE__ */ new WeakMap();
_validation = /* @__PURE__ */ new WeakMap();
_onInput = /* @__PURE__ */ new WeakSet();
onInput_fn = function() {
  __privateGet(this, _validation).validate(this.input.value ? this.input.value : void 0);
  this.valid = __privateGet(this, _validation).validity.valid;
  __privateGet(this, _validation).removeValidationMessage();
  this.input.toggleAttribute("invalid", !this.valid);
  this.input.toggleAttribute("valid", this.valid);
  if (this.valid) {
    this.input.setAttribute("aria-live", "polite");
  } else {
    this.input.removeAttribute("aria-live");
  }
};
LeafInput.styles = unsafeCSS(styles);
LeafInput.formAssociated = true;
__decorateClass([
  property()
], LeafInput.prototype, "autocomplete", 2);
__decorateClass([
  property({ type: Number, attribute: "maxlength" })
], LeafInput.prototype, "maxLength", 2);
__decorateClass([
  property({ type: Number, attribute: "minlength" })
], LeafInput.prototype, "minLength", 2);
__decorateClass([
  property({ type: Number, attribute: "min" })
], LeafInput.prototype, "min", 2);
__decorateClass([
  property({ type: Number, attribute: "max" })
], LeafInput.prototype, "max", 2);
__decorateClass([
  property()
], LeafInput.prototype, "pattern", 2);
__decorateClass([
  property()
], LeafInput.prototype, "placeholder", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafInput.prototype, "invalid", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafInput.prototype, "valid", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafInput.prototype, "readonly", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafInput.prototype, "disabled", 2);
__decorateClass([
  property({ reflect: true })
], LeafInput.prototype, "name", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafInput.prototype, "required", 2);
__decorateClass([
  property()
], LeafInput.prototype, "type", 2);
__decorateClass([
  property()
], LeafInput.prototype, "value", 2);
export {
  LeafInput
};
//# sourceMappingURL=input.component.js.map
